import cn from 'classnames';
import { isEqual } from 'lodash';
import { useRouter } from 'next/router';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { Icon, IconButton } from 'components';
import { ButtonV3, TabRadioGroupV2 } from 'components/ComponentV2';
import { confirmationModalInstance } from 'components/ComponentV2/ConfirmationModal';
import { DatePicker } from 'components/DatePickerV2/DatePicker.component';
import MultiSelect from 'components/MultiSelect';
import { DropdownV2, Input } from 'components/form';
import { Row } from 'components/layout';
import { ConfirmationButtonResponse } from 'hooks/UseCompanyCampaigns';
import { JobAttributes, JobRequestStep2 } from 'lib/models/job';
import { Option } from 'lib/models/option';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { REGEX } from 'lib/utils/regex';

import { useCampaigns } from 'lib/contexts/campaigns-context';
import { TouchpointState } from 'lib/models/opportunity';
import {
  formatDateToRetrieve,
  formatDateToSave,
  getCommaSeparatedNumber,
} from 'utils/format';
import DragAndDrop from '../../DragAndDrop';
import Header from '../../Header';
import { FieldStateOption } from '../Job.component';

import styles from '../Step.module.scss';

enum WorkType {
  FULL_TIME = 'Full time',
  PART_TIME = 'Part time',
}
const WorkTypeOption = [
  {
    id: '1',
    value: WorkType.FULL_TIME,
    label: WorkType.FULL_TIME,
  },
  {
    id: '2',
    value: WorkType.PART_TIME,
    label: WorkType.PART_TIME,
  },
];

export interface Step2Props {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  step: number;
  touchpoint: JobAttributes | null;
  educationRequirementsOptions: Array<Option>;
  currencyOptions: Array<Option>;
  onSave: (
    data: JobRequestStep2,
    isValueEdited: boolean,
    goToStep: number
  ) => void;
  isLoading: boolean;
}

export const Step2: React.FC<Step2Props> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    step,
    touchpoint,
    educationRequirementsOptions,
    currencyOptions,
    onSave,
    isLoading,
  }: Step2Props) => {
    const contentClassNames = cn(
      {
        [styles.Step]: true,
      },
      className
    );

    const router = useRouter();
    const { onboarding } = router.query;
    const [t] = useTranslation('opportunity.posting');
    const { mutateCompanyCampaigns } = useCampaigns();

    const [touchpointableId, setTouchpointableId] = useState<string | null>(
      null
    );
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [dateStates, setDateStates] = useState<{
      showStartDatePicker: boolean;
      showEndDatePicker: boolean;
      isStartDateHovered: boolean;
      isEndDateHovered: boolean;
    }>({
      showStartDatePicker: false,
      showEndDatePicker: false,
      isStartDateHovered: false,
      isEndDateHovered: false,
    });

    const [responsibilities, setResponsibilities] = useState<
      Array<FieldStateOption>
    >([
      {
        id: '1',
        value: '',
        isHovered: false,
        toBeFocussed: false,
      },
    ]);
    const [requirements, setRequirements] = useState<Array<FieldStateOption>>([
      {
        id: '1',
        value: '',
        isHovered: false,
        toBeFocussed: false,
      },
    ]);
    const [eduReqStates, setEduReqStates] = useState<{
      showDropdown: boolean;
      isHovered: boolean;
    }>({ showDropdown: false, isHovered: false });
    const [educationRequirement, setEducationRequirement] = useState<
      Array<string>
    >([]);
    const [workType, setWorkType] = useState(WorkType.FULL_TIME);
    const [compensationDetails, setCompensationDetails] = useState<string>('');
    const [minimumSalary, setMinimumSalary] = useState<string>('');
    const [maximumSalary, setMaximumSalary] = useState<string>('');
    const [payCurrency, setPayCurrency] = useState<string>('');
    const responsibilityContainerRef = useRef<HTMLDivElement | null>(null);
    const requirementContainerRef = useRef<HTMLDivElement | null>(null);

    const [availableTouchpointData, setAvailableTouchpointData] =
      useState<JobRequestStep2>();

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [shouldScroll, setShouldScroll] = useState(false);
    const isValidResponsibilities = responsibilities?.some((res) => res.value);
    const isValidRequirements = requirements?.some((res) => res.value);
    useEffect(() => {
      if (shouldScroll) {
        if (!isValidResponsibilities) {
          responsibilityContainerRef.current?.scrollIntoView({
            behavior: 'smooth',
          });
        }
        if (!isValidRequirements) {
          requirementContainerRef.current?.scrollIntoView({
            behavior: 'smooth',
          });
        }

        setShouldScroll(false); // Reset the state after scrolling
      }
    }, [shouldScroll, isValidResponsibilities, isValidRequirements]);

    useEffect(() => {
      if (touchpoint) {
        const {
          pay_currency,
          compensation_details,
          responsibilities: availableRes,
          requirements: availableReq,
          education_requirements: eduReqResponse,
        } = touchpoint;
        const {
          id: touchpointableIdResponse,
          start_date,
          end_date,
          full_time,
          minimum_salary,
          maximum_salary,
        } = parseResponse(touchpoint.touchpointable);

        const education_requirements = parseArrayResponse(eduReqResponse);
        setTouchpointableId(touchpointableIdResponse);
        // initial value of the variable full_time is null
        // and the variable worktype should only be modified
        // when full_time is assigned a value of true or false.
        if (typeof full_time === 'boolean') {
          setWorkType(full_time ? WorkType.FULL_TIME : WorkType.PART_TIME);
        }

        if (start_date) {
          setStartDate(formatDateToRetrieve(start_date));
          setDateStates({ ...dateStates, showStartDatePicker: true });
        }
        if (end_date) {
          setEndDate(formatDateToRetrieve(end_date));
          setDateStates({
            ...dateStates,
            showStartDatePicker: true,
            showEndDatePicker: true,
          });
        }
        if (availableRes.length > 0) {
          setResponsibilities(
            availableRes.map((item) => ({
              id: uuidv4(),
              value: item,
              isHovered: false,
            }))
          );
        }
        if (availableReq.length > 0)
          setRequirements(
            availableReq.map((item) => ({
              id: uuidv4(),
              value: item,
              isHovered: false,
            }))
          );

        if (education_requirements?.length > 0) {
          setEduReqStates({ showDropdown: true, isHovered: false });
          setEducationRequirement(
            education_requirements.map((item) => item.name)
          );
        }
        // salaries can be zero, so setting empty string
        // when it has falsy value to make placeholders visible
        setMinimumSalary(String(minimum_salary || ''));
        setMaximumSalary(String(maximum_salary || ''));
        setPayCurrency(pay_currency);
        setCompensationDetails(compensation_details);

        setAvailableTouchpointData({
          touchpointable_attributes: {
            id: touchpointableIdResponse,
            full_time,
            minimum_salary: String(minimum_salary || '').replace(/,/gi, ''),
            maximum_salary: String(maximum_salary || '').replace(/,/gi, ''),
            start_date: start_date
              ? formatDateToSave(formatDateToRetrieve(start_date))
              : null,
            end_date: end_date
              ? formatDateToSave(formatDateToRetrieve(end_date))
              : null,
          },
          pay_currency,
          compensation_details,
          responsibilities: availableRes,
          requirements: availableReq,
          education_requirement_ids: education_requirements.map(
            (item) => item.id
          ),
        });
      }
    }, [touchpoint]);

    const areValuesEdited = () => {
      if (touchpoint) {
        const eduReqId = educationRequirementsOptions
          .filter((item) => {
            if (educationRequirement.includes(item.label)) return item.id;
          })
          .map((item) => item.id);
        const selectedAttributes = {
          touchpointable_attributes: {
            id: touchpointableId,
            full_time: workType === WorkType.FULL_TIME ? true : false,
            minimum_salary: minimumSalary.replace(/,/gi, ''),
            maximum_salary: maximumSalary.replace(/,/gi, ''),
            start_date: startDate ? formatDateToSave(startDate) : null,
            end_date: endDate ? formatDateToSave(endDate) : null,
          },
          pay_currency: payCurrency,
          compensation_details: compensationDetails,
          responsibilities: responsibilities
            ?.map((item) => item.value)
            .filter((item) => item !== ''),
          requirements: requirements
            ?.map((item) => item.value)
            .filter((item) => item !== ''),
          education_requirement_ids: eduReqId,
        };
        return !isEqual(selectedAttributes, availableTouchpointData);
      } else {
        return true;
      }
    };

    const onClose = async () => {
      if (areValuesEdited()) {
        const flag = await confirmationModalInstance();
        if (flag === ConfirmationButtonResponse.SAVE) {
          onSubmit({ checkRequired: false, submitType: TouchpointState.SAVE });
        } else if (flag === ConfirmationButtonResponse.DONT_SAVE) {
          if (onboarding) {
            router.push(PAGE_ROUTES.RECRUITER_DASHBOARD);
            return;
          }
          router.push(PAGE_ROUTES.HIRING);
        }
      } else {
        if (onboarding) {
          router.push(PAGE_ROUTES.RECRUITER_DASHBOARD);
          return;
        }
        mutateCompanyCampaigns();
        router.push(PAGE_ROUTES.HIRING);
      }
    };

    const onNavigate = (goToStep: number) => {
      if (goToStep === step) return;
      if (goToStep === 1) {
        onSubmit({ checkRequired: false, goToStep });
      } else {
        onSubmit({ checkRequired: true, goToStep });
      }
    };

    const onSubmit = ({
      checkRequired,
      submitType,
      goToStep = 3,
    }: {
      checkRequired: boolean;
      submitType?: TouchpointState;
      goToStep?: number;
    }) => {
      const onSaveRequest = () => {
        const isValueEdited = areValuesEdited();
        const eduReqId = educationRequirementsOptions
          .filter((item) => {
            if (educationRequirement.includes(item.label)) return item.id;
          })
          .map((item) => item.id);

        const data = {
          touchpointable_attributes: {
            id: touchpointableId || '',
            start_date: startDate ? formatDateToSave(startDate) : null,
            end_date: endDate ? formatDateToSave(endDate) : null,
            full_time: workType === WorkType.FULL_TIME ? true : false,
            minimum_salary: minimumSalary.replace(/,/gi, ''),
            maximum_salary: maximumSalary.replace(/,/gi, ''),
          },
          pay_currency: payCurrency,
          compensation_details: compensationDetails,
          responsibilities: responsibilities
            ?.map((item) => item.value)
            .filter((item) => item !== ''),
          requirements: requirements
            ?.map((item) => item.value)
            .filter((item) => item !== ''),
          education_requirement_ids: eduReqId,
          submitType,
        };
        onSave(data, isValueEdited, goToStep);
      };
      if (goToStep > step) {
        if (!isValidResponsibilities) {
          setShouldScroll(true);
        }
        if (!isValidRequirements) {
          setShouldScroll(true);
        }
      }
      if (checkRequired) {
        setIsSubmitted(true);
        if (isInvalid) return;
      }
      onSaveRequest();
    };

    const maxSalaryNumber = maximumSalary.replace(/,/g, '');

    const isMaxSalaryInvalid = useMemo(() => {
      return Number(maxSalaryNumber) > 999900;
    }, [maxSalaryNumber]);

    const isInvalid =
      !isValidResponsibilities ||
      !isValidRequirements ||
      (minimumSalary ? !REGEX.SALARY.test(minimumSalary) : false) ||
      (maximumSalary ? !REGEX.SALARY.test(maximumSalary) : false) ||
      isMaxSalaryInvalid;

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      const keyCode = event.key;

      if (!REGEX.SALARY.test(keyCode)) {
        event.preventDefault();
      }
    };

    const isValidMaxSalary = useMemo(() => {
      return REGEX.SALARY.test(maxSalaryNumber) && !isMaxSalaryInvalid;
    }, [maximumSalary, minimumSalary, isMaxSalaryInvalid]);

    return (
      <>
        <Header
          step={step as number}
          onClose={onClose}
          onNavigate={(goToStep: number) => onNavigate(goToStep)}
          onNext={() => onNavigate(3)}
          onNextLoading={isLoading}
          className={styles.header}
        />
        <div className={contentClassNames} style={style} data-testid="Step1">
          <div className={styles.container}>
            <section className={styles.section}>
              <main className={styles.main}>
                <p className={styles.title}>{t('job-posting.work-type')}</p>
                <p className={styles.desc}>{t('job-posting.work-type-desc')}</p>
              </main>
              <div className={styles.separator}></div>
              <div className={styles.gap}></div>
              <div className={styles.gapWorktype}>
                <TabRadioGroupV2
                  title={t('job-posting.work-type')}
                  options={WorkTypeOption}
                  value={workType}
                  onChange={(value: string) => setWorkType(value as WorkType)}
                />
              </div>
              <article className={styles.article}>
                {dateStates.showStartDatePicker ? (
                  <div
                    onMouseEnter={() =>
                      setDateStates({
                        ...dateStates,
                        isStartDateHovered: true,
                      })
                    }
                    onMouseLeave={() =>
                      setDateStates({
                        ...dateStates,
                        isStartDateHovered: false,
                      })
                    }
                    className={cn(styles.dropdownContainer)}
                  >
                    <div className={styles.StepLabel}>
                      {t('job-posting.start-date')}
                    </div>
                    <DatePicker
                      placeholder={t('job-posting.select-start-date')}
                      minDate={new Date()}
                      value={startDate}
                      onChange={(value) => setStartDate(value)}
                      className={styles.dateContainer}
                    />
                    {dateStates.isStartDateHovered ? (
                      <IconButton
                        iconName="close"
                        size="xsmall"
                        onClick={() => {
                          setStartDate(null);
                          setEndDate(null);
                          setDateStates({
                            ...dateStates,
                            showStartDatePicker: false,
                            showEndDatePicker: false,
                          });
                        }}
                        className={cn(styles.remove, styles.dropdownRemove)}
                      />
                    ) : null}
                    <div className={styles.gap}></div>
                  </div>
                ) : null}
                {dateStates.showEndDatePicker && startDate ? (
                  <div
                    onMouseEnter={() =>
                      setDateStates({ ...dateStates, isEndDateHovered: true })
                    }
                    onMouseLeave={() =>
                      setDateStates({
                        ...dateStates,
                        isEndDateHovered: false,
                      })
                    }
                    className={cn(styles.dropdownContainer)}
                  >
                    <div className={styles.StepLabel}>
                      {t('job-posting.end-date')}
                    </div>
                    <DatePicker
                      placeholder={t('job-posting.select-end-date')}
                      minDate={startDate}
                      value={endDate}
                      onChange={(value) => setEndDate(value)}
                      className={styles.dateContainer}
                    />
                    {dateStates.isEndDateHovered ? (
                      <IconButton
                        iconName="close"
                        size="xsmall"
                        onClick={() => {
                          setEndDate(null);
                          setDateStates({
                            ...dateStates,
                            showEndDatePicker: false,
                          });
                        }}
                        className={cn(styles.remove, styles.dropdownRemove)}
                      />
                    ) : null}
                  </div>
                ) : null}
                <div className={styles.articleButton}>
                  <Row className={styles.buttonRow}>
                    {!dateStates.showStartDatePicker ? (
                      <ButtonV3
                        size="small"
                        color="secondary"
                        startIcon="plus"
                        onClick={() =>
                          setDateStates({
                            ...dateStates,
                            showStartDatePicker: true,
                          })
                        }
                      >
                        {t('job-posting.add-start-date')}
                      </ButtonV3>
                    ) : null}
                    {!dateStates.showEndDatePicker ? (
                      <ButtonV3
                        size="small"
                        color="secondary"
                        startIcon="plus"
                        onClick={() =>
                          setDateStates({
                            ...dateStates,
                            showEndDatePicker: true,
                          })
                        }
                        disabled={!startDate}
                      >
                        {t('job-posting.add-end-date')}
                      </ButtonV3>
                    ) : null}
                  </Row>
                  <Row align="center" wrap={false} className={styles.infoRow}>
                    <Icon iconName="information" size="xsmall" />
                    <p className={styles.info}>
                      {t('job-posting.work-type-info')}
                    </p>
                  </Row>
                </div>
              </article>
            </section>
            <section
              className={styles.section}
              ref={responsibilityContainerRef}
            >
              <main className={styles.main}>
                <div className={styles.inputContainer}>
                  <p className={styles.title}>
                    {t('job-posting.responsibilities')}
                  </p>
                  {isSubmitted && !isValidResponsibilities ? (
                    <p className={styles.required}>
                      {t('job-posting.required')}
                    </p>
                  ) : null}
                </div>
                <p className={styles.desc}>
                  {t('job-posting.responsibilities-desc')}
                </p>
              </main>
              <div className={styles.separator}></div>
              <article className={styles.article}>
                <DragAndDrop
                  placeholder="Add a responsibility"
                  fieldState={responsibilities}
                  setFieldState={setResponsibilities}
                />
                <div
                  className={cn({ [styles.gap]: responsibilities.length > 0 })}
                ></div>
                <ButtonV3
                  size="small"
                  color="secondary"
                  startIcon="plus"
                  onClick={() =>
                    setResponsibilities([
                      ...responsibilities,
                      {
                        id: uuidv4(),
                        value: '',
                        isHovered: false,
                        toBeFocussed: true,
                      },
                    ])
                  }
                  className={styles.articleButton}
                >
                  {t('job-posting.add-responsibility')}
                </ButtonV3>
              </article>
            </section>
            <section className={styles.section} ref={requirementContainerRef}>
              <main className={styles.main}>
                <div className={styles.inputContainer}>
                  <p className={styles.title}>
                    {t('job-posting.requirements')}
                  </p>
                  {isSubmitted && !isValidRequirements ? (
                    <p className={styles.required}>
                      {t('job-posting.required')}
                    </p>
                  ) : null}
                </div>
                <p className={styles.desc}>
                  {t('job-posting.requirements-info')}
                </p>
              </main>
              <div className={styles.separator}></div>
              <article className={styles.article}>
                <DragAndDrop
                  placeholder={t('job-posting.add-a-requirement')}
                  fieldState={requirements}
                  setFieldState={setRequirements}
                />
                <div
                  className={cn({ [styles.gap]: requirements.length > 0 })}
                ></div>
                {eduReqStates.showDropdown ? (
                  <div
                    onMouseEnter={() =>
                      setEduReqStates({ showDropdown: true, isHovered: true })
                    }
                    onMouseLeave={() =>
                      setEduReqStates({ showDropdown: true, isHovered: false })
                    }
                    className={styles.dropdownContainer}
                  >
                    <div className={styles.StepLabel}>
                      {t('job-posting.education-requirements')}
                    </div>
                    <MultiSelect
                      title={
                        educationRequirement?.join(', ') ||
                        t('job-posting.select-a-requirement')
                      }
                      options={educationRequirementsOptions}
                      values={educationRequirement}
                      onChange={(id) => setEducationRequirement(id)}
                      className={cn(styles.dropdown)}
                      dropdownClassName={styles.dropdownSelect}
                      showCount={false}
                    />
                    {eduReqStates.isHovered ? (
                      <IconButton
                        iconName="close"
                        size="xsmall"
                        onClick={() => {
                          setEduReqStates({
                            showDropdown: false,
                            isHovered: false,
                          });
                          setEducationRequirement([]);
                        }}
                        className={cn(styles.remove, styles.dropdownRemove)}
                      />
                    ) : null}
                  </div>
                ) : null}
                <div
                  className={cn({
                    [styles.gap]:
                      requirements.length > 0 || eduReqStates.showDropdown,
                  })}
                ></div>
                <Row className={styles.buttonRow}>
                  <ButtonV3
                    size="small"
                    color="secondary"
                    startIcon="plus"
                    onClick={() =>
                      setRequirements([
                        ...requirements,
                        {
                          id: uuidv4(),
                          value: '',
                          isHovered: false,
                          toBeFocussed: true,
                        },
                      ])
                    }
                    className={styles.articleButton}
                  >
                    {t('job-posting.add-requirement')}
                  </ButtonV3>
                  {!eduReqStates.showDropdown ? (
                    <ButtonV3
                      size="small"
                      color="secondary"
                      startIcon="plus"
                      onClick={() =>
                        setEduReqStates({
                          showDropdown: true,
                          isHovered: false,
                        })
                      }
                    >
                      {t('job-posting.add-education-requirement')}
                    </ButtonV3>
                  ) : null}
                </Row>
              </article>
            </section>
            <section className={styles.section}>
              <main className={styles.main}>
                <p className={styles.title}>
                  {t('job-posting.salary-range')}{' '}
                  <span>({t('job-posting.optional')})</span>
                </p>
                <p className={styles.desc}>
                  {t('job-posting.salary-range-desc')}
                </p>
              </main>
              <div className={styles.separator}></div>
              <main className={styles.main}>
                <Row className={styles.salaryRow}>
                  <div className={styles.salaryContainer}>
                    <Input
                      placeholder="20,000"
                      label={t('job-posting.from')}
                      value={getCommaSeparatedNumber(minimumSalary)}
                      onChange={(event) => setMinimumSalary(event.target.value)}
                      onKeyPress={handleKeyPress}
                      className={cn(styles.input)}
                      isValid={
                        minimumSalary ? REGEX.SALARY.test(minimumSalary) : true
                      }
                    />
                    <Row
                      wrap={false}
                      className={styles.salaryDropdownContainer}
                    >
                      <div className={styles.salarySeparator}></div>
                      <DropdownV2
                        label=""
                        title=""
                        value={payCurrency}
                        onChange={(value) => {
                          setPayCurrency(value);
                        }}
                        options={currencyOptions}
                        selectedValueLabel={payCurrency}
                        isValid={true}
                        className={styles.salaryDropdown}
                      />
                    </Row>
                  </div>
                  <div className={styles.salaryContainer}>
                    <Input
                      placeholder="50,000"
                      label={t('job-posting.to')}
                      value={getCommaSeparatedNumber(maximumSalary)}
                      onChange={(event) => setMaximumSalary(event.target.value)}
                      onKeyPress={handleKeyPress}
                      isValid={isValidMaxSalary}
                      className={cn(styles.input)}
                    />
                    <Row
                      wrap={false}
                      className={styles.salaryDropdownContainer}
                    >
                      <div className={styles.salarySeparator}></div>
                      <DropdownV2
                        label=""
                        title=""
                        value={payCurrency}
                        onChange={(value) => {
                          setPayCurrency(value);
                        }}
                        options={currencyOptions}
                        selectedValueLabel={payCurrency}
                        isValid={true}
                        className={styles.salaryDropdown}
                      />
                    </Row>
                  </div>
                </Row>
                <div className={styles.gap}></div>
                <Input
                  label={t('job-posting.additional-compensation-details')}
                  placeholder={t(
                    'job-posting.additional-compensation-details-placeholder'
                  )}
                  value={compensationDetails}
                  onChange={(event) =>
                    setCompensationDetails(event.target.value)
                  }
                  className={cn(styles.input)}
                />
                <Row align="center" className={styles.infoRow}>
                  <Icon iconName="information" size="xsmall" />
                  <p className={styles.info}>
                    {t('job-posting.salary-range-info')}
                  </p>
                </Row>
              </main>
            </section>
            <ButtonV3
              type="submit"
              onClick={() => onSubmit({ checkRequired: true })}
              className={styles.next}
              isLoading={isLoading}
              disabled={isSubmitted && isInvalid}
            >
              {t('job-posting.next')}
            </ButtonV3>
          </div>
        </div>
      </>
    );
  }
);

Step2.displayName = 'Step2';
